import { useState, useRef, cloneElement, type ReactElement } from "react";
import { Popover as AriaPopover, Button as AriaButton } from "react-aria-components";
import { cx } from "@/utils/cx";

interface EmojiPickerProps {
    onEmojiSelect: (emoji: string) => void;
    trigger: ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
}

// Lista di emoji comuni organizzati per categoria
const EMOJI_CATEGORIES = {
    smileys: ["😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃", "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😙", "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤭", "🤫", "🤔"],
    gestures: ["👋", "🤚", "🖐", "✋", "🖖", "👌", "🤌", "🤏", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "👍", "👎", "✊", "👊", "🤛", "🤜", "👏", "🙌", "👐", "🤲", "🤝"],
    objects: ["⌚", "📱", "📲", "💻", "⌨️", "🖥", "🖨", "🖱", "🖲", "🕹", "🗜", "💾", "💿", "📀", "📼", "📷", "📸", "📹", "🎥", "📽", "🎞", "📞", "☎️", "📟", "📠", "📺", "📻", "🎙", "🎚", "🎛"],
    symbols: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟", "☮️", "✝️", "☪️", "🕉", "☸️", "✡️", "🔯", "🕎", "☯️", "☦️", "🛐"],
    flags: ["🏳️", "🏴", "🏁", "🚩", "🏳️‍🌈", "🏳️‍⚧️", "🇺🇳", "🇦🇫", "🇦🇽", "🇦🇱", "🇩🇿", "🇦🇸", "🇦🇩", "🇦🇴", "🇦🇮", "🇦🇶", "🇦🇬", "🇦🇷", "🇦🇲", "🇦🇼", "🇦🇺", "🇦🇹", "🇦🇿", "🇧🇸", "🇧🇭", "🇧🇩", "🇧🇧", "🇧🇾", "🇧🇪", "🇧🇿"],
};

export const EmojiPicker = ({ onEmojiSelect, trigger }: EmojiPickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const handleEmojiClick = (emoji: string) => {
        onEmojiSelect(emoji);
        setIsOpen(false);
    };

    // Clone the trigger element and add the ref and onClick handler
    const triggerWithRef = cloneElement(trigger, {
        ref: triggerRef,
        onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            setIsOpen((prev) => !prev);
            // Call original onClick if it exists
            const originalOnClick = trigger.props?.onClick;
            if (originalOnClick && typeof originalOnClick === 'function') {
                originalOnClick(e);
            }
        },
    } as any);

    return (
        <>
            {triggerWithRef}
            {isOpen && (
                <AriaPopover
                    triggerRef={triggerRef}
                    placement="top"
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    className={({ isEntering, isExiting }) =>
                        cx(
                            "origin-(--trigger-anchor-point) will-change-transform rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt outline-hidden",
                            isEntering &&
                                "duration-150 ease-out animate-in fade-in placement-top:slide-in-from-bottom-0.5",
                            isExiting &&
                                "duration-100 ease-in animate-out fade-out placement-top:slide-out-to-bottom-0.5"
                        )
                    }
                >
                    <div className="w-80 max-h-96 overflow-y-auto p-3">
                        {/* Categorie */}
                        {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                            <div key={category} className="mb-4 last:mb-0">
                                <div className="text-xs font-medium text-tertiary mb-2 uppercase tracking-wide px-1">
                                    {category}
                                </div>
                                <div className="grid grid-cols-8 gap-1">
                                    {emojis.map((emoji, index) => (
                                        <AriaButton
                                            key={`${category}-${index}`}
                                            onPress={() => handleEmojiClick(emoji)}
                                            className={cx(
                                                "flex items-center justify-center size-8 rounded-md text-lg",
                                                "transition-colors hover:bg-secondary",
                                                "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
                                            )}
                                            aria-label={`Select emoji ${emoji}`}
                                        >
                                            {emoji}
                                        </AriaButton>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </AriaPopover>
            )}
        </>
    );
};
